import { _throwNotImplementedException } from './COM';
import { Rs232DisplayAdapter } from './Rs232DisplayAdapter';
import { Rs232Reply } from './Rs232Reply';
import { $ } from '../../../vendor/jquery';
import { objectHasOwnProperty } from '../../Utility';
import _ from 'underscore';

// <editor-fold desc="Enumerations">

/**
 * Enumeration for message type field in NecPacket
 * @readonly
 * @enum {number}
 */
var NecMessageType = {
    Command: 0x41,
    CommandReply: 0x42,
    GetParameter: 0x43,
    GetParameterReply: 0x44,
    SetParameter: 0x45,
    SetParameterReply: 0x46,
};

/**
 * Enumeration for command type field in NecCommand
 * @readonly
 * @enum {Uint8Array}
 */
var NecCommandType = {
    GetSerialNo: new Uint8Array([0x43, 0x32, 0x31, 0x36]), //'C' - '2' - '1' - '6'
    GetModelName: new Uint8Array([0x43, 0x32, 0x31, 0x37]), //'C' - '2' - '1' - '7'
    GetPowerStatus: new Uint8Array([0x30, 0x31, 0x44, 0x36]), //'0' - '1' - 'D' - '6'
    PowerControl: new Uint8Array([0x43, 0x32, 0x30, 0x33, 0x44, 0x36]), //'C' - '2' - '0' - '3' - 'D' - '6'
};

/**
 * Enumeration for power state commands
 * @readonly
 * @enum {Uint8Array}
 */
var NecPowerState = {
    On: new Uint8Array([0x30, 0x30, 0x30, 0x31]), // '0' - '0' - '0' - '1'
    Off: new Uint8Array([0x30, 0x30, 0x30, 0x34]), // '0' - '0' - '0' - '4'
};

/**
 * Enumeration for inputs
 * @readonly
 * @enum {number}
 */
var NecInput = {
    NoMean: 0x00,
    VGA: 0x01,
    RGB_HV: 0x02,
    DVI: 0x03,
    HDMISetOnly: 0x04,
    Video1: 0x05,
    Video2: 0x06,
    SVideo: 0x07,
    DVD_HD1: 0x0c,
    Option: 0x0d,
    DVD_HD2: 0x0e,
    DisplayPort: 0x0f,
    HDMI: 0x11,
    parseInt: function (value) {
        for (var prop in NecInput) {
            if (objectHasOwnProperty(NecInput, prop) && NecInput[prop] === value) {
                return prop;
            }
        }

        return 'unknown';
    },
};

/**
 * Enumeration for mute state
 * @readonly
 * @enum {Uint8Array}
 */
var NecMuteState = {
    Mute: new Uint8Array([0x30, 0x30, 0x30, 0x31]), // '0' - '0' - '0' - '1'
    Unmute: new Uint8Array([0x30, 0x30, 0x30, 0x32]), // '0' - '0' - '0' - '2'
};

/**
 * Enumeration for `get parameter` operations
 * @readonly
 * @enum {Uint8Array}
 */
var NecParameterOpCode = {
    Mute: new Uint8Array([0x30, 0x30, 0x38, 0x44]), //008D
    Volume: new Uint8Array([0x30, 0x30, 0x36, 0x32]), //0062
    Input: new Uint8Array([0x30, 0x30, 0x36, 0x30]), //0060
};

// </editor-fold>

// <editor-fold desc="NecRs232Reply Abstract Class">

/**
 * Generic Rs232Reply for NEC displays, represents a reply packet from the
 * display
 * @class
 * @param {number[]} bytes - the bytes received from the display
 */
function NecRs232Reply(bytes) {
    Rs232Reply.call(this, bytes);

    if (bytes.length < 9) {
        throw new Error('invalid packet from NEC display: response must be at least 9 bytes');
    }

    this.soh = bytes[0];

    this.reserved = bytes[1];

    this.monitorId = bytes[2];

    this.sender = bytes[3];

    this.command = bytes[4];

    this.messageLength = parseInt('0x' + String.fromCharCode(bytes[5], bytes[6]));

    if (bytes.length != this.messageLength + 8) {
        throw new Error(
            'invalid response from NEC display: packet must be (8 + ' +
                this.messageLength +
                ' = ' +
                (this.messageLength + 8) +
                ') long, actual size is ' +
                bytes.length
        );
    }

    this.message = bytes.slice(7, 7 + this.messageLength);

    this.blockCheckCode = bytes[7 + this.messageLength];
}

NecRs232Reply.prototype = Object.create(Rs232Reply.prototype);
NecRs232Reply.prototype.constructor = NecRs232Reply;

NecRs232Reply.prototype.verifyPacket = function () {
    if (this.soh !== 1) {
        throw new Error('invalid Start of Header byte');
    }

    var bcc = this.rawPacket[1];

    for (var i = 2; i < this.rawPacket.length - 1; ++i) {
        bcc = bcc ^ this.rawPacket[i];
    }

    if (bcc !== this.blockCheckCode) {
        throw new Error(
            'invalid Block Check Code. Expected `' + this.blockCheckCode + '`, got `' + bcc + '`'
        );
    }
};

NecRs232Reply.prototype.toString = _throwNotImplementedException('NecRs232Reply.toString');
// </editor-fold>

// <editor-fold desc="NecGetModelReply Class">
function NecGetModelReply(bytes) {
    NecRs232Reply.call(this, bytes);

    //verify the packet integrity
    this.verifyPacket();

    this.startOfMessage = this.message[0];

    this.replyType = String.fromCharCode.apply(String, this.message.slice(1, 5));

    if (this.replyType != 'C317') {
        throw new Error('invalid reply type. Expected `C317`, got `' + this.replyType + '`');
    }

    this.data = this.message.slice(5, this.message.length - 1);

    this.model = '';
    var modelName = String.fromCharCode.apply(String, this.data);
    for (var i = 0; i < modelName.length / 2 - 1; ++i) {
        var nextCharacter = parseInt(modelName.substr(i * 2, 2), 16);

        if (nextCharacter > 0) {
            this.model += String.fromCharCode(nextCharacter);
        } else {
            break;
        }
    }

    this.endOfMessage = this.message[this.message.length - 1];
}

NecGetModelReply.prototype = Object.create(NecRs232Reply.prototype);
NecGetModelReply.prototype.constructor = NecGetModelReply;

NecGetModelReply.prototype.toString = function () {
    return this.model;
};
// </editor-fold>

// <editor-fold desc="NecGetSerialNumberReply Class">
function NecGetSerialNumberReply(bytes) {
    NecRs232Reply.call(this, bytes);

    //verify the packet integrity
    this.verifyPacket();

    this.startOfMessage = this.message[0];

    this.replyType = String.fromCharCode.apply(String, this.message.slice(1, 5));

    if (this.replyType != 'C316') {
        throw new Error('invalid reply type. Expected `C316`, got `' + this.replyType + '`');
    }

    this.data = this.message.slice(5, this.message.length - 1);

    this.serialNumber = '';
    var serialNumber = String.fromCharCode.apply(String, this.data);
    for (var i = 0; i < serialNumber.length / 2 - 1; ++i) {
        var nextCharacter = parseInt(serialNumber.substr(i * 2, 2), 16);

        if (nextCharacter > 0) {
            this.serialNumber += String.fromCharCode(nextCharacter);
        } else {
            break;
        }
    }

    this.endOfMessage = this.message[this.message.length - 1];
}

NecGetSerialNumberReply.prototype = Object.create(NecRs232Reply.prototype);
NecGetSerialNumberReply.prototype.constructor = NecGetSerialNumberReply;

NecGetSerialNumberReply.prototype.toString = function () {
    return this.serialNumber;
};
// </editor-fold>

// <editor-fold desc="NecGetPowerStateReply Class">
function NecGetPowerStateReply(bytes) {
    NecRs232Reply.call(this, bytes);

    //verify the packet integrity
    this.verifyPacket();

    this.success = false;
    this.currentPowerMode = 'failure';

    this.startOfMessage = this.message[0];

    this.reserved2 = String.fromCharCode.apply(String, this.message.slice(1, 3));

    this.resultCode = parseInt(String.fromCharCode.apply(String, this.message.slice(3, 5)), 16);

    if (this.resultCode === 0) {
        this.success = true;

        this.displayPowerModeCode = String.fromCharCode.apply(String, this.message.slice(5, 7));

        this.parameterTypeCode = String.fromCharCode.apply(String, this.message.slice(7, 9));

        this.powerModeTypes = String.fromCharCode.apply(String, this.message.slice(9, 13));

        this.currentPowerMode = parseInt(
            String.fromCharCode.apply(String, this.message.slice(13, 17)),
            16
        );

        this.currentPowerModeTranslated = '';
        switch (this.currentPowerMode) {
            case 1:
                this.currentPowerModeTranslated = 'on';
                break;
            case 2:
                this.currentPowerModeTranslated = 'stand-by';
                break;
            case 3:
                this.currentPowerModeTranslated = 'suspend';
                break;
            case 4:
                this.currentPowerModeTranslated = 'off';
                break;
            default:
                this.currentPowerModeTranslated = 'unknown';
                break;
        }
    }

    this.endOfMessage = this.message[this.message.length - 1];
}

NecGetPowerStateReply.prototype = Object.create(NecRs232Reply.prototype);
NecGetPowerStateReply.prototype.constructor = NecGetPowerStateReply;

NecGetPowerStateReply.prototype.toString = function () {
    return this.currentPowerModeTranslated;
};
// </editor-fold>

// <editor-fold desc="NecSetPowerStateReply Class">
function NecSetPowerStateReply(bytes) {
    NecRs232Reply.call(this, bytes);

    //verify the packet integrity
    this.verifyPacket();

    this.success = false;
    this.currentPowerMode = 'failure';

    this.startOfMessage = this.message[0];

    this.resultCode = parseInt(String.fromCharCode.apply(String, this.message.slice(1, 3)), 16);

    if (this.resultCode === 0) {
        this.success = true;

        this.powerControlReplyCommand = String.fromCharCode.apply(String, this.message.slice(3, 9));

        this.currentPowerMode = parseInt(
            String.fromCharCode.apply(String, this.message.slice(9, 13)),
            16
        );

        this.currentPowerModeTranslated = '';
        switch (this.currentPowerMode) {
            case 1:
                this.currentPowerModeTranslated = 'on';
                break;
            case 2:
                this.currentPowerModeTranslated = 'stand-by';
                break;
            case 3:
                this.currentPowerModeTranslated = 'suspend';
                break;
            case 4:
                this.currentPowerModeTranslated = 'off';
                break;
            default:
                this.currentPowerModeTranslated = 'unknown';
                break;
        }
    }

    this.endOfMessage = this.message[this.message.length - 1];
}

NecSetPowerStateReply.prototype = Object.create(NecRs232Reply.prototype);
NecSetPowerStateReply.prototype.constructor = NecSetPowerStateReply;

NecSetPowerStateReply.prototype.toString = function () {
    return this.currentPowerModeTranslated;
};
// </editor-fold>

// <editor-fold desc="NecGetParameterReply Class">
function NecGetParameterReply(bytes) {
    NecRs232Reply.call(this, bytes);

    //verify the packet integrity
    this.verifyPacket();

    this.success = false;

    this.startOfMessage = this.message[0];

    this.resultCode = parseInt(String.fromCharCode.apply(String, this.message.slice(1, 3)), 16);

    if (this.resultCode === 0) {
        this.success = true;

        this.operationCodePage = String.fromCharCode.apply(String, this.message.slice(3, 5));

        this.operationCode = String.fromCharCode.apply(String, this.message.slice(5, 7));

        this.operationTypeCode = String.fromCharCode.apply(String, this.message.slice(7, 9));

        //todo: i believe these are always ASCII-encoded integers, but I'm not
        //sure. we'll do the integer conversion
        this.maximumValue = parseInt(
            String.fromCharCode.apply(String, this.message.slice(9, 13)),
            16
        );
        this.requestedValue = parseInt(
            String.fromCharCode.apply(String, this.message.slice(13, 17)),
            16
        );
    }

    this.endOfMessage = this.message[this.message.length - 1];
}

NecGetParameterReply.prototype = Object.create(NecRs232Reply.prototype);
NecGetParameterReply.prototype.constructor = NecGetParameterReply;

NecGetParameterReply.prototype.toString = _throwNotImplementedException(
    'NecGetParameterReply.toString'
);
// </editor-fold>

// <editor-fold desc="NecGetMutedReply Class">
function NecGetMutedReply(bytes) {
    NecGetParameterReply.call(this, bytes);
}

NecGetMutedReply.prototype = Object.create(NecGetParameterReply.prototype);
NecGetMutedReply.prototype.constructor = NecGetMutedReply;

NecGetMutedReply.prototype.toString = function () {
    if (!this.success) {
        return 'failure';
    }

    switch (this.requestedValue) {
        case 0:
        case 2:
            return 'unmute';
        case 1:
            return 'mute';
        default:
            return 'unknown';
    }
};
// </editor-fold>

// <editor-fold desc="NecGetVideoInputReply Class">
function NecGetVideoInputReply(bytes) {
    NecGetParameterReply.call(this, bytes);
}

NecGetVideoInputReply.prototype = Object.create(NecGetParameterReply.prototype);
NecGetVideoInputReply.prototype.constructor = NecGetVideoInputReply;

NecGetVideoInputReply.prototype.toString = function () {
    if (!this.success) {
        return 'failure';
    }

    return NecInput.parseInt(this.requestedValue);
};
// </editor-fold>

// <editor-fold desc="NecGetVolumeReply Class">
function NecGetVolumeReply(bytes) {
    NecGetParameterReply.call(this, bytes);
}

NecGetVolumeReply.prototype = Object.create(NecGetParameterReply.prototype);
NecGetVolumeReply.prototype.constructor = NecGetVolumeReply;

NecGetVolumeReply.prototype.toString = function () {
    if (!this.success) {
        return -1;
    }

    return this.requestedValue;
};
// </editor-fold>

// <editor-fold desc="NecSetParameterReply Class">
function NecSetParameterReply(bytes) {
    NecRs232Reply.call(this, bytes);

    //verify the packet integrity
    this.verifyPacket();

    this.success = false;
    this.requestedValue = 'failure';

    this.startOfMessage = this.message[0];

    this.resultCode = parseInt(String.fromCharCode.apply(String, this.message.slice(1, 3)), 16);

    if (this.resultCode === 0) {
        this.success = true;

        this.replyCommand = String.fromCharCode.apply(String, this.message.slice(3, 9));

        this.maximumValue = parseInt(
            String.fromCharCode.apply(String, this.message.slice(9, 13)),
            16
        );

        this.requestedValue = parseInt(
            String.fromCharCode.apply(String, this.message.slice(13, 17)),
            16
        );
    }

    this.endOfMessage = this.message[this.message.length - 1];
}

NecSetParameterReply.prototype = Object.create(NecRs232Reply.prototype);
NecSetParameterReply.prototype.constructor = NecSetParameterReply;

NecSetParameterReply.prototype.toString = function () {
    if (!this.stringValue) {
        NecRs232Reply.toString.call(this); //what would base class do?
    }

    return this.stringValue;
};
// </editor-fold>

// <editor-fold desc="NecSetAudioVolumeReply Class">
function NecSetAudioVolumeReply(bytes) {
    NecSetParameterReply.call(this, bytes);

    this.translatedValue = 'failure';

    if (this.success) {
        this.translatedValue = String(this.requestedValue);
    }
}

NecSetAudioVolumeReply.prototype = Object.create(NecRs232Reply.prototype);
NecSetAudioVolumeReply.prototype.constructor = NecSetAudioVolumeReply;

NecSetAudioVolumeReply.prototype.toString = function () {
    return this.translatedValue;
};
// </editor-fold>

// <editor-fold desc="NecSetMutedReply Class">
function NecSetMutedReply(bytes) {
    NecSetParameterReply.call(this, bytes);

    this.translatedValue = 'failure';

    switch (this.requestedValue) {
        case 0:
        case 2:
            this.translatedValue = 'unmute';
            break;
        case 1:
            this.translatedValue = 'mute';
            break;
        default:
            this.translatedValue = 'unknown';
            break;
    }
}

NecSetMutedReply.prototype = Object.create(NecRs232Reply.prototype);
NecSetMutedReply.prototype.constructor = NecSetMutedReply;

NecSetMutedReply.prototype.toString = function () {
    return this.translatedValue;
};
// </editor-fold>

// <editor-fold desc="NecCommand Class">
function NecCommand(command, data) {
    NecPacket.call(this, NecMessageType.Command);

    var messageData = new Uint8Array(command.length + (data ? data.length : 0));
    messageData.set(command);

    if (data) {
        messageData.set(data, command.length);
    }

    this.message = messageData;
}

NecCommand.prototype = Object.create(NecPacket.prototype);
NecCommand.prototype.constructor = NecCommand;
// </editor-fold>

// <editor-fold desc="NecSetParameter Class">
function NecSetParameter(opCode, newValue) {
    NecPacket.call(this, NecMessageType.SetParameter);

    var messageData = new Uint8Array(opCode.length + (newValue ? newValue.length : 0));
    messageData.set(opCode);

    if (newValue) {
        messageData.set(newValue, opCode.length);
    }

    this.message = messageData;
}

NecSetParameter.prototype = Object.create(NecPacket.prototype);
NecSetParameter.prototype.constructor = NecSetParameter;
// </editor-fold>

// <editor-fold desc="NecGetParameter Class">
function NecGetParameter(opCode) {
    NecPacket.call(this, NecMessageType.GetParameter);

    this.message = opCode;
}

NecGetParameter.prototype = Object.create(NecPacket.prototype);
NecGetParameter.prototype.constructor = NecGetParameter;
// </editor-fold>

// <editor-fold desc="NecPacket">
function NecPacket(messageType, destination, source) {
    this.startOfHeader = 0x01;

    this.reserved = 0x30;

    this.destination = destination || 0x41; //default destination ID `1`

    this.source = source || 0x30; //default source is `0` (the controller)

    if (!messageType) {
        throw new Error('must specify messageType parameter in NecPacket()');
    }

    this.messageType = messageType;

    this.messageLength = 0;

    this.startOfMessage = 0x02;

    this.message = new Uint8Array(0);

    this.endOfMessage = 0x03;

    this.blockCheckCode = 0;

    this.packetDelimiter = 0x0d;
}

NecPacket.prototype.toUint8Array = function () {
    var i;

    var messageLength = (this.message.length + 2).toString(16).toUpperCase();
    messageLength = ('00' + messageLength).substring(messageLength.length);
    this.messageLength = new Uint8Array([messageLength.charCodeAt(0), messageLength.charCodeAt(1)]);

    var payload = new Uint8Array(11 + this.message.length);
    payload[0] = this.startOfHeader;
    payload[1] = this.reserved;
    payload[2] = this.destination;
    payload[3] = this.source;
    payload[4] = this.messageType;
    payload[5] = this.messageLength[0];
    payload[6] = this.messageLength[1];
    payload[7] = this.startOfMessage;

    for (i = 0; i < this.message.length; ++i) {
        payload[8 + i] = this.message[i];
    }

    payload[8 + this.message.length] = this.endOfMessage;

    var bcc = payload[1];
    for (i = 2; i < payload.length - 2; ++i) {
        bcc = bcc ^ payload[i];
    }

    payload[8 + this.message.length + 1] = bcc;

    payload[8 + this.message.length + 2] = this.packetDelimiter;

    return payload;
};
// </editor-fold>

/**
 * Generic NecRs232DisplayAdapter
 * @class
 * @augments Rs232DisplayAdapter
 * @param {string} displayMake - the vendor name of the hardware display
 * @param {string|null} displayModel - the model of the display
 * @param {Rs232Controller} rs232Controller - controller for interacting with
 * COM port
 */
function NecRs232DisplayAdapter(displayMake, displayModel, rs232Controller) {
    this.make = 'NEC';

    this.model = displayModel;

    this.controller = rs232Controller;
}

NecRs232DisplayAdapter.prototype = Object.create(Rs232DisplayAdapter.prototype);
NecRs232DisplayAdapter.prototype.constructor = NecRs232DisplayAdapter;

// <editor-fold desc="Rs232DisplayAdapter Methods">

NecRs232DisplayAdapter.prototype.getModel = function () {
    var jqDeferred = $.Deferred();

    this.waitForIdle().then(
        function () {
            this.controller.write(new NecCommand(NecCommandType.GetModelName).toUint8Array());

            return Promise.resolve()
                .then(
                    function () {
                        return this.controller.readLine();
                    }.bind(this)
                )
                .then(
                    function (response) {
                        try {
                            var result = new NecGetModelReply(response);

                            jqDeferred.resolve(result);
                        } catch (replyParseError) {
                            jqDeferred.reject(replyParseError);
                        }
                    }.bind(this),
                    jqDeferred.reject
                );
        }.bind(this),
        jqDeferred.reject
    );

    return jqDeferred.promise();
};

NecRs232DisplayAdapter.prototype.getSerialNumber = function () {
    var jqDeferred = $.Deferred();

    this.waitForIdle().then(
        function () {
            this.controller.write(new NecCommand(NecCommandType.GetSerialNo).toUint8Array());

            return Promise.resolve()
                .then(
                    function () {
                        return this.controller.readLine();
                    }.bind(this)
                )
                .then(
                    function (response) {
                        var result = new NecGetSerialNumberReply(response);

                        jqDeferred.resolve(result);
                    }.bind(this)
                );
        }.bind(this),
        jqDeferred.reject
    );

    return jqDeferred.promise();
};

NecRs232DisplayAdapter.prototype.getPowerState = function () {
    var jqDeferred = $.Deferred();

    this.waitForIdle().then(
        function () {
            this.controller.write(new NecCommand(NecCommandType.GetPowerStatus).toUint8Array());

            return Promise.resolve()
                .then(
                    function () {
                        return this.controller.readLine();
                    }.bind(this)
                )
                .then(
                    function (response) {
                        var result = new NecGetPowerStateReply(response);

                        if (!result.success) {
                            throw new Error(
                                'GetPowerStatus NEC command failed with error code `' +
                                    result.resultCode +
                                    '`'
                            );
                        }

                        jqDeferred.resolve(result);
                    }.bind(this)
                );
        }.bind(this),
        jqDeferred.reject
    );

    return jqDeferred.promise();
};

NecRs232DisplayAdapter.prototype.setPowerState = function (desiredPowerState) {
    var jqDeferred = $.Deferred();

    this.waitForIdle().then(
        function () {
            desiredPowerState = desiredPowerState.toLowerCase();
            if (desiredPowerState !== 'on' && desiredPowerState !== 'off') {
                jqDeferred.reject(
                    'invalid requested power state `' +
                        desiredPowerState +
                        '`. must be either `on` or `off`'
                );
            }

            this.controller.write(
                new NecCommand(
                    NecCommandType.PowerControl,
                    desiredPowerState === 'on' ? NecPowerState.On : NecPowerState.Off
                ).toUint8Array()
            );

            return Promise.resolve()
                .then(
                    function () {
                        return this.controller.readLine();
                    }.bind(this)
                )
                .then(
                    function (response) {
                        var result = new NecSetPowerStateReply(response);

                        if (!result.success) {
                            throw new Error(
                                'PowerControl NEC command failed with error code `' +
                                    result.resultCode +
                                    '`'
                            );
                        }

                        this.setRefractoryPeriod(10000);

                        jqDeferred.resolve(result);
                    }.bind(this)
                );
        }.bind(this),
        jqDeferred.reject
    );

    return jqDeferred.promise();
};

NecRs232DisplayAdapter.prototype.getMuted = function () {
    var jqDeferred = $.Deferred();

    this.waitForIdle().then(
        function () {
            this.controller.write(new NecGetParameter(NecParameterOpCode.Mute).toUint8Array());

            return Promise.resolve()
                .then(
                    function () {
                        return this.controller.readLine();
                    }.bind(this)
                )
                .then(
                    function (response) {
                        var result = new NecGetMutedReply(response);

                        if (!result.success) {
                            throw new Error(
                                'NecGetParameter(`NecParameterOpCode.Mute`) request failed with error code `' +
                                    result.resultCode +
                                    '`'
                            );
                        }

                        jqDeferred.resolve(result);
                    }.bind(this)
                );
        }.bind(this),
        jqDeferred.reject
    );

    return jqDeferred.promise();
};

NecRs232DisplayAdapter.prototype.setMuted = function (desiredMuteState) {
    var jqDeferred = $.Deferred();

    this.waitForIdle().then(
        function () {
            desiredMuteState = desiredMuteState.toLowerCase();
            if (desiredMuteState !== 'mute' && desiredMuteState !== 'unmute') {
                jqDeferred.reject(
                    'invalid requested mute state `' +
                        desiredMuteState +
                        '`. must be either `mute` or `unmute`'
                );
            }

            this.controller.write(
                new NecSetParameter(
                    NecParameterOpCode.Mute,
                    desiredMuteState === 'mute' ? NecMuteState.Mute : NecMuteState.Unmute
                ).toUint8Array()
            );

            return Promise.resolve()
                .then(
                    function () {
                        return this.controller.readLine();
                    }.bind(this)
                )
                .then(function (response) {
                    var result = new NecSetMutedReply(response);

                    if (!result.success) {
                        throw new Error(
                            'NecSetParameter(`NecParameterOpCode.Mute`) request failed with error code `' +
                                result.resultCode +
                                '`'
                        );
                    }

                    jqDeferred.resolve(result);
                });
        }.bind(this),
        jqDeferred.reject
    );

    return jqDeferred.promise();
};

NecRs232DisplayAdapter.prototype.getAudioVolume = function () {
    var jqDeferred = $.Deferred();

    this.waitForIdle().then(
        function () {
            this.controller.write(new NecGetParameter(NecParameterOpCode.Volume).toUint8Array());

            return Promise.resolve()
                .then(
                    function () {
                        return this.controller.readLine();
                    }.bind(this)
                )
                .then(function (response) {
                    var result = new NecGetVolumeReply(response);

                    if (!result.success) {
                        throw new Error(
                            'NecGetParameter(`NecParameterOpCode.Volume`) request failed with error code `' +
                                result.resultCode +
                                '`'
                        );
                    }

                    jqDeferred.resolve(result);
                });
        }.bind(this),
        jqDeferred.reject
    );

    return jqDeferred.promise();
};

NecRs232DisplayAdapter.prototype.setAudioVolume = function (desiredAudioVolume) {
    var jqDeferred = $.Deferred();

    this.waitForIdle().then(
        function () {
            desiredAudioVolume = parseInt(desiredAudioVolume);

            return Promise.resolve()
                .then(
                    function () {
                        return this.getAudioVolume();
                    }.bind(this)
                )
                .catch(function () {
                    return { maximumValue: 100 };
                })
                .then(
                    function (getAudioVolumeResponse) {
                        var maximumAudioValue = getAudioVolumeResponse.maximumValue;

                        if (desiredAudioVolume < 0 || desiredAudioVolume > maximumAudioValue) {
                            throw new Error(
                                'invalid requested volume `' +
                                    desiredAudioVolume +
                                    '`. must be between 0 and ' +
                                    maximumAudioValue
                            );
                        }

                        //encode desiredAudioVolume as ASCII characters
                        desiredAudioVolume = desiredAudioVolume.toString(16).toUpperCase();
                        desiredAudioVolume = ('0000' + desiredAudioVolume).substring(
                            desiredAudioVolume.length
                        );
                        desiredAudioVolume = new Uint8Array(
                            _.map(desiredAudioVolume.split(''), function (character) {
                                return character.charCodeAt(0);
                            })
                        );

                        this.controller.write(
                            new NecSetParameter(
                                NecParameterOpCode.Volume,
                                desiredAudioVolume
                            ).toUint8Array()
                        );

                        return this.controller.readLine();
                    }.bind(this)
                )
                .then(function (response) {
                    var result = new NecSetAudioVolumeReply(response);

                    if (!result.success) {
                        throw new Error(
                            'NecSetParameter(`NecParameterOpCode.Volume`) request failed with error code `' +
                                result.resultCode +
                                '`'
                        );
                    }

                    jqDeferred.resolve(result);
                });
        }.bind(this),
        jqDeferred.reject
    );

    return jqDeferred.promise();
};

NecRs232DisplayAdapter.prototype.getVideoInput = function () {
    var jqDeferred = $.Deferred();

    this.waitForIdle().then(
        function () {
            this.controller.write(new NecGetParameter(NecParameterOpCode.Input).toUint8Array());

            return Promise.resolve()
                .then(
                    function () {
                        return this.controller.readLine();
                    }.bind(this)
                )
                .then(function (response) {
                    var result = new NecGetVideoInputReply(response);

                    if (!result.success) {
                        throw new Error(
                            'NecGetParameter(`NecParameterOpCode.Input`) request failed with error code `' +
                                result.resultCode +
                                '`'
                        );
                    }

                    jqDeferred.resolve(result);
                });
        }.bind(this),
        jqDeferred.reject
    );

    return jqDeferred.promise();
};

NecRs232DisplayAdapter.prototype.setVideoInput = function (desiredVideoInput) {
    var jqDeferred = $.Deferred();

    this.waitForIdle().then(
        function () {
            var originalReadTimeout;

            return Promise.resolve()
                .then(
                    function () {
                        if (!NecInput[desiredVideoInput]) {
                            throw new Error(
                                'invalid requested input `' +
                                    desiredVideoInput +
                                    '`. Must be one of NecInput enumeration.'
                            );
                        }

                        //encode desiredAudioVolume as ASCII characters
                        desiredVideoInput = NecInput[desiredVideoInput].toString(16);
                        desiredVideoInput = ('0000' + desiredVideoInput).substring(
                            desiredVideoInput.length
                        );
                        desiredVideoInput = new Uint8Array(
                            _.map(desiredVideoInput.split(''), function (character) {
                                return character.charCodeAt(0);
                            })
                        );

                        this.controller.write(
                            new NecSetParameter(
                                NecParameterOpCode.Input,
                                desiredVideoInput
                            ).toUint8Array()
                        );

                        //NOTE: this command can take quite some time if switching to VGA
                        originalReadTimeout = this.controller.readTimeout;
                        this.controller.readTimeout = 5000;

                        return this.controller.readLine();
                    }.bind(this)
                )
                .catch(
                    function (e) {
                        this.controller.readTimeout = originalReadTimeout;

                        throw e;
                    }.bind(this)
                )
                .then(
                    function (response) {
                        this.controller.readTimeout = originalReadTimeout;

                        var result = new NecSetParameterReply(response);

                        if (!result.success) {
                            throw new Error(
                                'NecSetParameter(`NecParameterOpCode.Input`) request failed with error code `' +
                                    result.resultCode +
                                    '`'
                            );
                        }

                        this.setRefractoryPeriod(7000);

                        //NOTE: we should probably subclass NecSetParameterReply and do this
                        //in toString function...
                        result.stringValue = NecInput.parseInt(result.requestedValue);

                        jqDeferred.resolve(result);
                    }.bind(this)
                );
        }.bind(this),
        jqDeferred.reject
    );

    return jqDeferred.promise();
};

// </editor-fold>

//tell the Rs232DisplayAdapter service about the "NEC" brand of displays
Rs232DisplayAdapter.registerVendorAdapter('nec', NecRs232DisplayAdapter);

export { NecRs232DisplayAdapter };
